import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMissionControlLogsViewModel } from "./MissionControlLogs.vm";

export const MissionControlLogs: React.FC = () => {
	const vm = useMissionControlLogsViewModel();

	if (!vm.isSuperUser) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Typography variant="h5" gutterBottom>
					Mission Control Logs
				</Typography>

				<Card variant="outlined">
					<CardContent>
						<Stack spacing={3}>
							{/* User Selection */}
							<Autocomplete
								options={vm.teamMembers}
								getOptionLabel={(option) =>
									option.name && option.missionControlUsername
										? `${option.name} (${option.missionControlUsername})`
										: option.name || option.missionControlUsername || "Unknown"
								}
								value={vm.selectedUser}
								onChange={(_, newValue) => {
									vm.setSelectedUser(newValue);
									vm.clearMessages();
								}}
								loading={vm.loadingTeamMembers}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Select User"
										required
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{vm.loadingTeamMembers ? <CircularProgress color="inherit" size={20} /> : null}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
								disabled={vm.loading || vm.loadingTeamMembers}
							/>

							{/* Date Range Selection */}
							<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
								<TextField
									label="Past N Hours"
									type="number"
									value={vm.pastHours}
									onChange={(e) => {
										const value = parseInt(e.target.value);
										if (!isNaN(value)) {
											vm.handlePastHoursChange(value);
										}
									}}
									disabled={vm.loading || vm.loadingTeamMembers}
									inputProps={{ min: 1, step: 1 }}
									helperText="Quick range selector"
									fullWidth
								/>
								<DateTimePicker
									label="Start Date & Time"
									value={vm.startDate}
									onChange={(newValue) => {
										vm.setStartDate(newValue);
										vm.clearMessages();
									}}
									disabled={vm.loading || vm.loadingTeamMembers}
									slotProps={{
										textField: {
											required: true,
											fullWidth: true,
										},
									}}
								/>
								<DateTimePicker
									label="End Date & Time"
									value={vm.endDate}
									onChange={(newValue) => {
										vm.setEndDate(newValue);
										vm.clearMessages();
									}}
									disabled={vm.loading || vm.loadingTeamMembers}
									minDateTime={vm.startDate || undefined}
									slotProps={{
										textField: {
											required: true,
											fullWidth: true,
										},
									}}
								/>
							</Stack>

							{/* Error/Success Messages */}
							{vm.error && (
								<Alert severity="error" onClose={vm.clearMessages}>
									{vm.error}
								</Alert>
							)}
							{vm.successMessage && (
								<Alert severity="success" onClose={vm.clearMessages}>
									{vm.successMessage}
								</Alert>
							)}

							{/* Download Buttons */}
							<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
								<Button
									variant="contained"
									color="primary"
									startIcon={vm.loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
									onClick={() => vm.handleDownload("csv")}
									disabled={vm.isDownloadDisabled}
									fullWidth
								>
									{vm.loading ? "Downloading..." : "Download as CSV"}
								</Button>
								<Button
									variant="contained"
									color="primary"
									startIcon={vm.loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
									onClick={() => vm.handleDownload("tsv")}
									disabled={vm.isDownloadDisabled}
									fullWidth
								>
									{vm.loading ? "Downloading..." : "Download as TSV"}
								</Button>
								<Button
									variant="contained"
									color="primary"
									startIcon={vm.loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
									onClick={() => vm.handleDownload("json")}
									disabled={vm.isDownloadDisabled}
									fullWidth
								>
									{vm.loading ? "Downloading..." : "Download as JSON"}
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</Box>
		</LocalizationProvider>
	);
};
